import React, { useState, useEffect, useRef } from "react";
import Swal from 'sweetalert2';
import useApiRequest from './useApiRequest';
import ProgressBarTimer from "./ProgressBarTimer";
import ShuffleIcon from "./atoms/ShuffleIcon";
import ReplaceIcon from "./atoms/ReplaceIcon";
import SocialShare from "./SocialShare";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from 'react-i18next';
import * as Tone from 'tone';

import AccountSettingsModal from "./Account";
import LeaderboardModal from "./Leaderboard";
import LeaguesPage from "./LeaguesPage";
import SettingsModal from "./SettingsModal";
import SplashHelpModal from "./SplashHelpModal";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import LeaderBoardButton from "./atoms/LeaderBoardButton";
import LeaguesButton from "./atoms/LeaguesButton.js";
import SettingsButton from "./atoms/SettingsButton";
import AccountIcon from "./atoms/AccountIcon";
import HelpButton from "./atoms/HelpButton";

// Import Welsh Digraph Utilities
import { tokenizeWelshWord, isWelshVowel, countVowelsInTiles } from "./utils/utils";

const ACCENT_MAP = {
    'A': ['A', 'Â', 'Á', 'Ä', 'À'],
    'E': ['E', 'Ê', 'É', 'Ë', 'È'],
    'I': ['I', 'Î', 'Í', 'Ï', 'Ì'],
    'O': ['O', 'Ô', 'Ó', 'Ö', 'Ò'],
    'U': ['U', 'Û', 'Ú', 'Ü', 'Ù'],
    'W': ['W', 'Ŵ', 'Ẃ', 'Ẅ', 'Ẁ'],
    'Y': ['Y', 'Ŷ', 'Ý', 'Ÿ', 'Ỳ'],
};

const letterWeights = {
    //Vowels
    'A' : 10.54, 'E' : 6.73, 'I' : 8.92, 'O' : 5.94, 'U' : 2.15, 'W' : 4.34, 'Y' : 5.63,

    //Consonants
    'B' : 1.93, 'C' : 4.15, 'D' : 7.68, 'F' : 3.30, 'G' : 3.86, 'H' : 3.08, 'J' : 0.04,
    'L' : 6.57, 'M' : 2.50, 'N' : 6.35, 'P' : 1.40, 'R' : 7.10, 'S' : 4.09, 'T' : 3.65,

};

const Panagram = ({
                      playerId, playerName, player, setPlayer,
                      setPlayerId, setPlayerName, setIsGuest,
                      setPlayerPrefReceiveNewsletter, setPlayerPrefReceivePrompts,
                      openAccountModalOnGameLoad, setOpenAccountModalOnGameLoad,
                      handleOpenLeaguesModal,
                      hasPlayedToday, setHasPlayedToday
                  }) => {
    const baseUrl = process.env.REACT_APP_API_URL;
    const apiUrl = baseUrl + '/api';
    const { makeRequest } = useApiRequest(apiUrl);

    const [vowels, setVowels] = useState([]);
    const [consonants, setConsonants] = useState([]);
    const [letters, setLetters] = useState([]); // 9 tiles currently on board
    const [inputLetters, setInputLetters] = useState([]); // Array of selected tile tokens
    const [message, setMessage] = useState({ text: null, autoDismiss: 0, isError: false });
    const [submittedWords, setSubmittedWords] = useState([]);
    const [lastSubmittedWord, setLastSubmittedWord] = useState("");
    const [score, setScore] = useState(0);
    const [clickedLetters, setClickedLetters] = useState([]);

    const [isGameOver, setIsGameOver] = useState(false);
    const [game, setGame] = useState(null);
    const [gameOverStats, setGameOverStats] = useState(null);
    const [nextEmptyIndex, setNextEmptyIndex] = useState(0);
    const [isValidating, setIsValidating] = useState(false);

    const [timerKey, setTimerKey] = useState(Date.now());
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);

    const [vowelIndexState, setVowelIndex] = useState(0);
    const [consonantIndexState, setConsonantIndex] = useState(0);
    const [vowelFrequencyArray, setVowelFrequencyArray] = useState([]);

    const isGameOverHandled = useRef(false);
    const audioContextStartedRef = useRef(false);

    const { t, i18n } = useTranslation();
    const exchangedText = t('submitted_words.exchanged');

    const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
    const [isLeaderboardModalOpen, setIsLeaderboardModalOpen] = useState(false);
    const [isLeaguesModalOpen, setIsLeaguesModalOpen] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [isSplashHelpModalOpen, setIsSplashHelpModalOpen] = useState(false);

    useEffect(() => {
        setStartTime(Date.now());
    }, []);

    useEffect(() => {
        fetch(`${apiUrl}/daily-letters`)
            .then((response) => response.json())
            .then((data) => {
                setVowels(data.vowels);
                setConsonants(data.consonants);
                setVowelFrequencyArray(data.vowelFrequency);
                initializeLetters(data.vowels, data.consonants, data.vowelFrequency);
            })
            .catch((error) => console.error("Error fetching letters:", error));
    }, [apiUrl]);

    useEffect(() => {
        const nextEmpty = inputLetters.findIndex((letter) => !letter);
        setNextEmptyIndex(nextEmpty === -1 ? inputLetters.length : nextEmpty);
    }, [inputLetters]);

    const startAudioContext = async () => {
        if (Tone.context.state !== 'running' && !audioContextStartedRef.current) {
            try {
                await Tone.start();
                audioContextStartedRef.current = true;
            } catch (e) {
                console.error("Could not start Tone.js audio context:", e);
            }
        }
    };

    const shuffleArray = (array) => {
        const arr = [...array];
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
    };

    const initializeLetters = (vowelArray, consonantArray, vowelFreqArray) => {
        const targetVowels = vowelFreqArray[0] !== undefined ? vowelFreqArray[0] : 4;
        let selectedVowels = [];
        let selectedConsonants = [];
        let currentVowelIdx = 0;
        let currentConsonantIdx = 0;

        for (let i = 0; i < targetVowels; i++) {
            if (currentVowelIdx < vowelArray.length) {
                selectedVowels.push(vowelArray[currentVowelIdx]);
                currentVowelIdx++;
            }
        }

        const numConsonantsNeeded = 9 - selectedVowels.length;
        for (let i = 0; i < numConsonantsNeeded; i++) {
            if (currentConsonantIdx < consonantArray.length) {
                selectedConsonants.push(consonantArray[currentConsonantIdx]);
                currentConsonantIdx++;
            }
        }

        setVowelIndex(currentVowelIdx);
        setConsonantIndex(currentConsonantIdx);

        let initialLetters = [...selectedVowels, ...selectedConsonants];
        while (initialLetters.length < 9) {
            initialLetters.push('?');
        }
        initialLetters = shuffleArray(initialLetters.slice(0, 9));
        setLetters(initialLetters);
    };

    const shuffleLetters = () => {
        setLetters(prev => shuffleArray(prev));
        setClickedLetters([]);
        setInputLetters([]);
        startAudioContext();
    };

    const handleClick = (index, letterTile) => {
        if (!isGameOver && inputLetters.length < 9) {
            if (!clickedLetters.some(item => item.index === index)) {
                setClickedLetters(prev => [...prev, { index, letter: letterTile }]);
                setInputLetters(prev => [...prev, letterTile]);
                startAudioContext();
            }
        }
    };

    const handleDelete = () => {
        if (clickedLetters.length > 0) {
            setInputLetters(prev => prev.slice(0, -1));
            setClickedLetters(prev => prev.slice(0, -1));
        }
    };

    const handleEnter = async () => {
        if (isValidating || inputLetters.length === 0 || submittedWords.length >= 5) return;

        setIsValidating(true);
        startAudioContext();

        const submittedWordString = inputLetters.join(""); // Combines digraphs e.g. ["CH", "A"] -> "CHA"
        const isValid = await validateWord(submittedWordString);

        if (isValid) {
            setMessage({ text: t('word.valid', { word: submittedWordString }), autoDismiss: 3, isError: false });
            setSubmittedWords(prev => [...prev, submittedWordString]);
            setLastSubmittedWord(submittedWordString);
        } else {
            setMessage({ text: t('word.invalid', { word: submittedWordString }), autoDismiss: 3, isError: true });
        }

        setClickedLetters([]);
        setInputLetters([]);
        setIsValidating(false);
    };

    /**
     * Refactored Replacement Logic with Digraph Awareness
     */
    const replaceUsedLetters = (submittedWordStr) => {
        let tempCurrentLetters = [...letters];
        // Parse submitted word into digraph-aware tokens
        let usedTokens = tokenizeWelshWord(submittedWordStr);
        let lettersToKeep = [];

        tempCurrentLetters.forEach(tile => {
            const indexInSubmitted = usedTokens.indexOf(tile);
            if (indexInSubmitted > -1) {
                usedTokens.splice(indexInSubmitted, 1);
            } else {
                lettersToKeep.push(tile);
            }
        });

        const roundIndex = submittedWords.length;
        const targetVowels = vowelFrequencyArray[roundIndex] !== undefined
            ? vowelFrequencyArray[roundIndex]
            : 4;

        const currentVowelsInKeep = countVowelsInTiles(lettersToKeep);
        const numNewLettersNeeded = 9 - lettersToKeep.length;

        let newVowelsToDraw = Math.max(0, targetVowels - currentVowelsInKeep);
        let newConsonantsToDraw = numNewLettersNeeded - newVowelsToDraw;

        if (newConsonantsToDraw < 0) {
            newVowelsToDraw = numNewLettersNeeded;
            newConsonantsToDraw = 0;
        }

        newVowelsToDraw = Math.min(newVowelsToDraw, vowels.length - vowelIndexState);
        newConsonantsToDraw = Math.min(newConsonantsToDraw, consonants.length - consonantIndexState);

        let drawnVowels = [];
        let drawnConsonants = [];
        let nextVowelIdx = vowelIndexState;
        let nextConsonantIdx = consonantIndexState;

        for (let i = 0; i < newVowelsToDraw; i++) {
            if (nextVowelIdx < vowels.length) {
                drawnVowels.push(vowels[nextVowelIdx]);
                nextVowelIdx++;
            }
        }
        for (let i = 0; i < newConsonantsToDraw; i++) {
            if (nextConsonantIdx < consonants.length) {
                drawnConsonants.push(consonants[nextConsonantIdx]);
                nextConsonantIdx++;
            }
        }

        setVowelIndex(nextVowelIdx);
        setConsonantIndex(nextConsonantIdx);

        let newSetOfLetters = [...lettersToKeep, ...drawnVowels, ...drawnConsonants];
        while (newSetOfLetters.length < 9) {
            newSetOfLetters.push('?');
        }

        setLetters(shuffleArray(newSetOfLetters.slice(0, 9)));
    };

    const handleExchange = () => {
        if (inputLetters.length > 0) {
            Swal.fire({
                title: t('alert.titles.sure'),
                text: t('alert.exchange.text'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('alert.exchange.confirm'),
                cancelButtonText: t('alert.exchange.cancel'),
            }).then((result) => {
                if (result.isConfirmed) {
                    const wordStr = inputLetters.join("");
                    setSubmittedWords([...submittedWords, 'WORD-SKIPPED']);
                    setLastSubmittedWord(wordStr);
                    setInputLetters([]);
                    setClickedLetters([]);
                    startAudioContext();
                    Swal.fire(t('alert.exchange.success.title'), t('alert.exchange.success.text'), 'success');
                }
            });
        }
    };

    useEffect(() => {
        const processGameUpdate = async () => {
            if (lastSubmittedWord.length > 0 && !isGameOverHandled.current) {
                if (submittedWords.length >= 5) {
                    setIsGameOver(true);
                    setEndTime(Date.now());
                    isGameOverHandled.current = true;
                    const updatedData = await updateGameStatus(true);
                    if (updatedData) setGameOverStats(updatedData.stats);
                } else {
                    replaceUsedLetters(lastSubmittedWord);
                    if (lastSubmittedWord !== 'WORD-SKIPPED') {
                        updateGameStatus(false);
                    }
                }
            }
        };
        processGameUpdate();
    }, [submittedWords, lastSubmittedWord]);

    const updateGameStatus = async (isGameComplete = false) => {
        try {
            let responseData;
            const payload = {
                user_id: parseInt(playerId),
                complete: isGameComplete ? 1 : 0,
                score: score,
                words_used: submittedWords,
                start_timestamp: startTime,
                end_timestamp: isGameComplete ? Date.now() : undefined
            };

            if (!game && playerId) {
                responseData = await makeRequest(`/games`, 'POST', payload);
            } else if (playerId) {
                responseData = await makeRequest(`/games/${game.id}/update`, 'POST', payload);
            }

            if (responseData && responseData.success) {
                setGame(responseData.game);
                return responseData;
            }
            return null;
        } catch (error) {
            console.error("Failed to update game status:", error);
            return null;
        }
    };

    const validateWord = async (word) => {
        try {
            const data = await makeRequest(`/validate-word?lang=cy&word=${word}`, 'GET');
            if (data.success && data.valid) {
                setScore(prev => prev + calculateWordScore(word));
                return true;
            }
            return false;
        } catch (error) {
            console.error("Error validating word:", error);
            return false;
        }
    };

    /**
     * Calculates word score by scoring each individual character (e.g. DDUON -> D, D, U, O, N)
     * Matching Laravel and React Native scoring parity.
     */
    const calculateWordScore = (wordStr, baseValue = 20) => {
        if (!wordStr) return 0;

        const tokens = tokenizeWelshWord(wordStr);
        const fullWordStr = wordStr.toUpperCase();

        let score = tokens.length * 2;

        if (tokens.length === 9) {
            score += 25;
        }

        // Iterate through individual raw characters
        for (let i = 0; i < fullWordStr.length; i++) {
            const char = fullWordStr[i];

            const baseChar = Object.keys(ACCENT_MAP).find(key =>
                key === char || ACCENT_MAP[key].includes(char)
            ) || char;

            const weight = letterWeights[baseChar] ?? 0;
            score += (baseValue - weight);
        }

        return Math.round(score);
    };

    return (
        <div className="game-container">
            <LanguageSwitcher />
            <div className={`game-block ${isGameOver ? "game-over" : ''}`}>
                <ProgressBarTimer
                    key={timerKey}
                    totalTime={300}
                    isSplashHelpModalOpen={isSplashHelpModalOpen}
                    isGameOver={isGameOver}
                    onTimeUp={() => { setIsGameOver(true); setEndTime(Date.now()); updateGameStatus(true); }}
                    gameScore={score}
                    submittedWords={submittedWords}
                />

                {/* Daily Letter Tiles */}
                <div className="tile-container daily-letters">
                    {letters.map((letterTile, index) => (
                        <div
                            key={index}
                            className={`tile ${clickedLetters.some(item => item.index === index) ? "disabled" : ""}`}
                            onClick={() => handleClick(index, letterTile)}
                        >
                            <span className="front">{letterTile}</span>
                            <span className="back">{letterTile}</span>
                        </div>
                    ))}
                </div>

                <div className="letter-action-container">
                    <button className="shuffle-button" onClick={shuffleLetters} disabled={isGameOver}>
                        <ShuffleIcon />&nbsp;{t('buttons.shuffle')}
                    </button>
                    <button className="delete" onClick={handleDelete} disabled={isGameOver}>
                        ⌫<span> {t('buttons.delete_letter')}</span>
                    </button>
                    <button className="exchange-button" onClick={handleExchange} disabled={isGameOver || inputLetters.length === 0}>
                        <ReplaceIcon />&nbsp;{t('buttons.exchange')}
                    </button>
                </div>

                <hr className="word-divider" />

                {/* 9 Input Slots */}
                <div className="tile-container new-word">
                    {[...Array(9)].map((_, index) => (
                        <div
                            key={index}
                            className={`tile ${inputLetters[index] ? "filled" : "empty"}`}
                            style={{
                                border: inputLetters[index] || index === nextEmptyIndex ? '2px solid #3498db' : '2px solid #ddd',
                            }}
                        >
                            {inputLetters[index] || ""}
                        </div>
                    ))}
                </div>

                <div className="keyboard-row">
                    <button className="key special enter" onClick={handleEnter} disabled={isGameOver || isValidating}>
                        {isValidating ? t("buttons.check_word") : t("buttons.submit")}
                    </button>
                </div>
            </div>

            {message.text && (
                <div className={`message-box message ${message.isError ? "error" : "success"} ${message.text ? "fade-in" : "fade-out"}`}>
                    {message.text}
                </div>
            )}

            <div className="submitted-words">
                <h3>{t('submitted_words.title')}</h3>
                <div className="word-blocks">
                    {[...Array(5)].map((_, index) => (
                        <div
                            key={index}
                            className={`word-block ${submittedWords[index] === "WORD-SKIPPED" ? "skipped" : submittedWords[index] ? "filled" : "empty"}`}
                        >
                            {submittedWords[index] === "WORD-SKIPPED" ? exchangedText : submittedWords[index] || ""}
                        </div>
                    ))}
                </div>
            </div>

            <footer className="mobile-footer">
                <div className="footer-items-container">
                    {player ? (
                        <div className="footer-item mobile-footer__account">
                            <button className="user account" title={t('footer.tabs.account')} onClick={() => setIsAccountModalOpen(true)}>
                                <FontAwesomeIcon icon={faUser} size="2x" color="#333" />
                            </button>
                            <p className="footer-item-text">{playerName ? playerName : t('footer.player-label', { playerName: playerId })}</p>
                        </div>
                    ) : (
                        <div className="footer-item mobile-footer__account">
                            <button className="user signup" title={t('footer.tabs.account')} onClick={() => setIsAccountModalOpen(true)}>
                                <AccountIcon isLoggedIn={!!player} />
                            </button>
                            <p className="footer-item-text">{t('footer.tabs.account')}</p>
                        </div>
                    )}
                    <div className="footer-item mobile-footer__leaderboard">
                        <LeaderBoardButton setIsLeaderboardModalOpen={setIsLeaderboardModalOpen} />
                        <p className="footer-item-text">{t('footer.tabs.leaderboard')}</p>
                    </div>
                    <div className="footer-item mobile-footer__leagues">
                        <LeaguesButton setIsLeaguesModalOpen={setIsLeaguesModalOpen} />
                        <p className="footer-item-text">{t('footer.tabs.leagues')}</p>
                    </div>
                    <div className="footer-item mobile-footer__settings">
                        <SettingsButton setIsSettingsModalOpen={setIsSettingsModalOpen} />
                        <p className="footer-item-text">{t('footer.tabs.settings')}</p>
                    </div>
                    <div className="footer-item mobile-footer__help">
                        <HelpButton setIsSplashHelpModalOpen={setIsSplashHelpModalOpen} />
                        <p className="footer-item-text">{t('footer.tabs.help')}</p>
                    </div>

                    <AccountSettingsModal
                        isOpen={isAccountModalOpen || openAccountModalOnGameLoad}
                        onClose={() => setIsAccountModalOpen(false)}
                        onSignupSuccess={(id) => { setPlayerId(id); localStorage.setItem('playerId', id); }}
                        onLoginSuccess={(pData) => { setPlayer(pData); setPlayerName(pData.nickname); setPlayerId(pData.id); }}
                        player={player}
                    />
                    <LeaderboardModal
                        playerId={playerId}
                        isOpen={isLeaderboardModalOpen}
                        onClose={() => setIsLeaderboardModalOpen(false)}
                        hasPlayedToday={hasPlayedToday}
                    />
                    <LeaguesPage
                        playerId={playerId}
                        isOpen={isLeaguesModalOpen}
                        onClose={() => setIsLeaguesModalOpen(false)}
                    />
                    <SettingsModal
                        isOpen={isSettingsModalOpen}
                        onClose={() => setIsSettingsModalOpen(false)}
                    />
                    <SplashHelpModal
                        isOpen={isSplashHelpModalOpen}
                        onClose={() => setIsSplashHelpModalOpen(false)}
                    />
                </div>
            </footer>
        </div>
    );
};

export default Panagram;